import {useCallback, useMemo} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {ShoppingCart, UserCheck} from 'lucide-react';
import {useSummary} from 'context/summary';
import {PATHS} from 'routes/constants';
import {DEAL_STATUSES, OFFER_CATEGORIES, SUMMARY_PAYMENTS_STATUSES} from '@chekin/core';
import {useReservation} from 'context/reservation';
import {useFirstGuestMustBeAdult} from 'hooks/useFirstGuestMustBeAdult';
import {useFetchDeals} from '../useFetchDeals';
import {useFetchOffers} from '../useFetchOffers';
import {usePaymentsCart} from '../payments/usePaymentsCart';
import {usePaymentsEntryPath} from '../payments/usePaymentsEntryPath';
import {getRegistrationPath} from './getRegistrationPath';

enum NAVIGATION_STATUSES {
  complete = 'COMPLETE',
  incomplete = 'INCOMPLETE',
}

const getBookingPaymentsStatus = (...statuses: Array<string | null | undefined>) =>
  statuses
    .filter(status => !!status && status !== SUMMARY_PAYMENTS_STATUSES.inactive)
    .every(status => status === SUMMARY_PAYMENTS_STATUSES.complete);

function useBuildNavigationItems() {
  const {t} = useTranslation();
  const {data, isSuccess} = useSummary();
  const {data: reservation} = useReservation();
  const {data: paginatedPetFeeOffers} = useFetchOffers({
    housingId: reservation?.housing_id,
    category: OFFER_CATEGORIES.petFee,
  });
  const {data: paginatedDealsList} = useFetchDeals({reservationId: reservation?.id});
  const {paymentsCart} = usePaymentsCart({refetchOnMount: false});
  const hasPayableCartItems = Boolean(paymentsCart?.pay_now_payments?.length);

  const hasPetFeeOffer =
    Boolean(paginatedPetFeeOffers?.count && paginatedPetFeeOffers.count > 0) &&
    Boolean(reservation?.number_of_pets && reservation.number_of_pets > 0);

  const isPetFeeDealPaid =
    hasPetFeeOffer &&
    paginatedDealsList?.results.some(
      deal =>
        deal.offer.category === OFFER_CATEGORIES.petFee &&
        (deal.status === DEAL_STATUSES.paid || deal.status === DEAL_STATUSES.requested),
    );

  const paymentsEntryPath = usePaymentsEntryPath();
  const {firstGuestMustBeAdult: shouldFirstGuestBeAdult} = useFirstGuestMustBeAdult();

  const isSummaryBookingPaymentsCompleted = getBookingPaymentsStatus(
    data?.taxes?.status,
    data?.property_protection?.status,
    data?.booking_payments?.status,
  );
  const isBookingPaymentsCompleted =
    isSummaryBookingPaymentsCompleted && (!hasPetFeeOffer || isPetFeeDealPaid);

  const getPaymentPath = useCallback(
    () =>
      isBookingPaymentsCompleted && !hasPayableCartItems
        ? PATHS.payments.ordersHistory
        : paymentsEntryPath,
    [isBookingPaymentsCompleted, hasPayableCartItems, paymentsEntryPath],
  );

  const navigationItems = useMemo(() => {
    if (!isSuccess) return [];
    const isCompletedGuestRegistration =
      data?.guest_registration?.status === NAVIGATION_STATUSES.complete;

    return [
      {
        title: 'guest_registration',
        testId: 'guest-registration-nav-item',
        completed: isCompletedGuestRegistration,
        show: /*data?.guest_registration?.editable*/ true,
        path: getRegistrationPath(),
        icon: (
          <UserCheck
            size={20}
            color="var(--icon-primary)"
            className="kiosko:w-[54px] kiosko:h-[54px]"
          />
        ),
        notification: shouldFirstGuestBeAdult
          ? {
              title: t('first_guest_must_be_an_adult_title'),
              message: <Trans i18nKey="first_guest_must_be_an_adult_message" />,
            }
          : undefined,
      },
      {
        title: 'payments',
        testId: 'booking-payments-nav-item',
        completed: isBookingPaymentsCompleted,
        show: [
          data?.property_protection?.active,
          data?.taxes.active,
          data?.booking_payments.active,
          hasPetFeeOffer,
        ].some(Boolean),
        path: getPaymentPath(),
        icon: <ShoppingCart size={20} color="var(--icon-primary)" />,
      },
    ];
  }, [
    data,
    isSuccess,
    getPaymentPath,
    isBookingPaymentsCompleted,
    hasPetFeeOffer,
    shouldFirstGuestBeAdult,
    t,
  ]);

  const displayedNavigationItems = useMemo(() => {
    return navigationItems.filter(navItem => navItem.show);
  }, [navigationItems]);

  return {
    navigationItems,
    displayedNavigationItems,
    getPaymentPath,
    isLoading: !isSuccess,
    getRegistrationPath,
  };
}

export {useBuildNavigationItems};
export {NAVIGATION_STATUSES};
